import type { WeekDaySetting, WeekDaySettingInput } from '@repo/frontend-shared';
import { DAYS_OF_WEEK, defaultAvailability } from '@repo/ui/fixtures/mentorAvailability/constants/time';
import type { DayAvailability } from '@repo/ui/fixtures/mentorAvailability/types/time';

type AvailabilityState = Record<string, DayAvailability>;

// Map UI day names to backend indices (0=Sunday, 1=Monday, ..., 6=Saturday)
const dayToIndex: Record<string, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

const indexToDay: Record<number, keyof AvailabilityState> = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

export function frontendToBackend(availability: AvailabilityState): WeekDaySettingInput[] {
  // Ensure all 7 days are included and consistently ordered
  return DAYS_OF_WEEK.map(dayName => {
    const data = availability[dayName] ?? defaultAvailability[dayName];
    const weekDayIndex = dayToIndex[dayName];
    if (weekDayIndex === undefined) {
      throw new Error(`Invalid day name: ${dayName}`);
    }
    return {
      weekDay: weekDayIndex,
      isAvailable: data.isAvailable,
      intervals: data.isAvailable ? data.timeRanges.map(range => ({ start: range.start, end: range.end })) : [],
    };
  });
}

export function backendToFrontend(settings: WeekDaySetting[]): AvailabilityState {
  // Start from UI defaults to guarantee all keys are present (Monday..Sunday)
  const merged: AvailabilityState = { ...defaultAvailability };

  for (const setting of settings) {
    const idx = setting.weekDay;
    if (typeof idx !== 'number' || idx < 0 || idx > 6) continue;
    const dayName = indexToDay[idx];
    const timeRanges = setting.isAvailable
      ? setting.intervals.map(interval => ({ start: interval.start, end: interval.end }))
      : [];
    merged[dayName] = {
      isAvailable: setting.isAvailable,
      timeRanges,
    };
  }

  // Ensure ordering matches the UI component expectations (Monday through Sunday)
  const ordered: AvailabilityState = {} as AvailabilityState;
  for (const day of DAYS_OF_WEEK) {
    ordered[day] = merged[day];
  }
  return ordered;
}
